import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Switch,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { Power, ShieldAlert, AlertTriangle } from 'lucide-react';
import {
  useCodeAgentKillSwitches,
  useRevokeAllCodeAgentSessions,
  useUpdateCodeAgentKillSwitches,
} from '../../services/code-agent.service';
import {
  CodeAgentBrowserToolsSettings,
  CodeAgentRemoteSettings,
  CodeAgentPromptBudgetSettings,
  CodeAgentRiskLevel,
  CodeAgentSkillSource,
  CodeAgentWebToolsSettings,
} from '../../types/code-agent';
import {
  CodeAgentPromptBudgetSection,
  DEFAULT_PROMPT_BUDGET,
  DEFAULT_WEB_TOOLS,
} from './CodeAgentPromptBudgetSection';
import { CodeAgentBrowserToolsSection, DEFAULT_BROWSER_TOOLS } from './CodeAgentBrowserToolsSection';
import { CodeAgentRemoteSection, DEFAULT_REMOTE } from './CodeAgentRemoteSection';
import { FormattedNumberTextField } from '../../components/common/FormattedNumberTextField';
import { codeAgentFieldSx } from './codeAgentFieldStyles';

const RISK_LEVELS: CodeAgentRiskLevel[] = [
  'write',
  'execute',
  'network',
  'destructive',
  'credential-sensitive',
];

const SKILL_SOURCES: CodeAgentSkillSource[] = ['project', 'user', 'admin', 'system', 'plugin'];

const linesToList = (value: string) =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const listToLines = (value: string[] = []) => value.join('\n');

export const CodeAgentKillSwitchesPage: React.FC = () => {
  const { data: response, isLoading } = useCodeAgentKillSwitches();
  const updateMutation = useUpdateCodeAgentKillSwitches();
  const revokeAllMutation = useRevokeAllCodeAgentSessions();

  const config = response?.data;
  const [globalEnabled, setGlobalEnabled] = useState(config?.enabled ?? true);
  const [forceSafeMode, setForceSafeMode] = useState(config?.forceApprovalMode ?? false);
  const [minCliVersion, setMinCliVersion] = useState(config?.minCliVersion ?? '0.1.0');
  const [recommendedCliVersion, setRecommendedCliVersion] = useState(
    config?.recommendedCliVersion ?? '',
  );
  const [blockedRiskLevels, setBlockedRiskLevels] = useState<CodeAgentRiskLevel[]>(
    config?.blockedRiskLevels ?? [],
  );
  const [localSkillsEnabled, setLocalSkillsEnabled] = useState(
    config?.localSkillsEnabled ?? true,
  );
  const [userSkillsEnabled, setUserSkillsEnabled] = useState(
    config?.userSkillsEnabled ?? true,
  );
  const [implicitSkillActivationEnabled, setImplicitSkillActivationEnabled] = useState(
    config?.implicitSkillActivationEnabled ?? true,
  );
  const [skillReferenceLoadingEnabled, setSkillReferenceLoadingEnabled] = useState(
    config?.skillReferenceLoadingEnabled ?? true,
  );
  const [blockedSkillSources, setBlockedSkillSources] = useState<CodeAgentSkillSource[]>(
    config?.blockedSkillSources ?? [],
  );
  const [blockedSkillRiskLevels, setBlockedSkillRiskLevels] = useState<CodeAgentRiskLevel[]>(
    config?.blockedSkillRiskLevels ?? [],
  );
  const [blockedSkillsWithScripts, setBlockedSkillsWithScripts] = useState(
    config?.blockedSkillsWithScripts ?? false,
  );
  const [blockedSkillNames, setBlockedSkillNames] = useState(
    listToLines(config?.blockedSkillNames ?? []),
  );
  const [blockedSkillIds, setBlockedSkillIds] = useState(
    listToLines(config?.blockedSkillIds ?? []),
  );
  const [blockedProjectSkillPaths, setBlockedProjectSkillPaths] = useState(
    listToLines(config?.blockedProjectSkillPaths ?? []),
  );
  const [allowedPluginNames, setAllowedPluginNames] = useState(
    listToLines(config?.allowedPluginNames ?? []),
  );
  const [blockedPluginNames, setBlockedPluginNames] = useState(
    listToLines(config?.blockedPluginNames ?? []),
  );
  const [maxIterationsLow, setMaxIterationsLow] = useState<number>(
    config?.maxIterationsByMode?.low ?? 25,
  );
  const [maxIterationsMedium, setMaxIterationsMedium] = useState<number>(
    config?.maxIterationsByMode?.medium ?? 40,
  );
  const [maxIterationsHigh, setMaxIterationsHigh] = useState<number>(
    config?.maxIterationsByMode?.high ?? 60,
  );
  const [promptBudget, setPromptBudget] = useState<CodeAgentPromptBudgetSettings>(
    config?.promptBudget ?? DEFAULT_PROMPT_BUDGET,
  );
  const [webTools, setWebTools] = useState<CodeAgentWebToolsSettings>(
    config?.webTools ?? DEFAULT_WEB_TOOLS,
  );
  const [browserTools, setBrowserTools] = useState<CodeAgentBrowserToolsSettings>(
    config?.browserTools ?? DEFAULT_BROWSER_TOOLS,
  );
  const [remote, setRemote] = useState<CodeAgentRemoteSettings>(config?.remote ?? DEFAULT_REMOTE);
  const [turnBudgetMode, setTurnBudgetMode] = useState<'tokens' | 'iterations'>(
    config?.turnBudget?.mode ?? 'tokens',
  );
  const [turnBudgetLow, setTurnBudgetLow] = useState<number>(
    config?.turnBudget?.tokensByMode?.low ?? 300000,
  );
  const [turnBudgetMedium, setTurnBudgetMedium] = useState<number>(
    config?.turnBudget?.tokensByMode?.medium ?? 800000,
  );
  const [turnBudgetHigh, setTurnBudgetHigh] = useState<number>(
    config?.turnBudget?.tokensByMode?.high ?? 2000000,
  );
  const [turnBudgetWarningPercent, setTurnBudgetWarningPercent] = useState<number>(
    config?.turnBudget?.warningPercent ?? 80,
  );
  
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);

  React.useEffect(() => {
    if (!config) return;
    setGlobalEnabled(config.enabled);
    setForceSafeMode(config.forceApprovalMode);
    setMinCliVersion(config.minCliVersion);
    setRecommendedCliVersion(config.recommendedCliVersion ?? '');
    setBlockedRiskLevels(config.blockedRiskLevels);
    setLocalSkillsEnabled(config.localSkillsEnabled ?? true);
    setUserSkillsEnabled(config.userSkillsEnabled ?? true);
    setImplicitSkillActivationEnabled(config.implicitSkillActivationEnabled ?? true);
    setSkillReferenceLoadingEnabled(config.skillReferenceLoadingEnabled ?? true);
    setBlockedSkillSources(config.blockedSkillSources ?? []);
    setBlockedSkillRiskLevels(config.blockedSkillRiskLevels ?? []);
    setBlockedSkillsWithScripts(config.blockedSkillsWithScripts ?? false);
    setBlockedSkillNames(listToLines(config.blockedSkillNames ?? []));
    setBlockedSkillIds(listToLines(config.blockedSkillIds ?? []));
    setBlockedProjectSkillPaths(listToLines(config.blockedProjectSkillPaths ?? []));
    setAllowedPluginNames(listToLines(config.allowedPluginNames ?? []));
    setBlockedPluginNames(listToLines(config.blockedPluginNames ?? []));
    setMaxIterationsLow(config.maxIterationsByMode?.low ?? 25);
    setMaxIterationsMedium(config.maxIterationsByMode?.medium ?? 40);
    setMaxIterationsHigh(config.maxIterationsByMode?.high ?? 60);
    setTurnBudgetMode(config.turnBudget?.mode ?? 'tokens');
    setTurnBudgetLow(config.turnBudget?.tokensByMode?.low ?? 300000);
    setTurnBudgetMedium(config.turnBudget?.tokensByMode?.medium ?? 800000);
    setTurnBudgetHigh(config.turnBudget?.tokensByMode?.high ?? 2000000);
    setTurnBudgetWarningPercent(config.turnBudget?.warningPercent ?? 80);
    setPromptBudget(config.promptBudget ?? DEFAULT_PROMPT_BUDGET);
    setWebTools(config.webTools ?? DEFAULT_WEB_TOOLS);
    setBrowserTools(config.browserTools ?? DEFAULT_BROWSER_TOOLS);
    setRemote(config.remote ?? DEFAULT_REMOTE);
  }, [config]);

  const handleSaveSettings = async () => {
    await updateMutation.mutateAsync({
      enabled: globalEnabled,
      forceApprovalMode: forceSafeMode,
      minCliVersion,
      recommendedCliVersion,
      blockedRiskLevels,
      localSkillsEnabled,
      userSkillsEnabled,
      implicitSkillActivationEnabled,
      skillReferenceLoadingEnabled,
      blockedSkillSources,
      blockedSkillNames: linesToList(blockedSkillNames),
      blockedSkillIds: linesToList(blockedSkillIds),
      blockedSkillRiskLevels,
      blockedSkillsWithScripts,
      blockedProjectSkillPaths: linesToList(blockedProjectSkillPaths),
      allowedPluginNames: linesToList(allowedPluginNames),
      blockedPluginNames: linesToList(blockedPluginNames),
      maxIterationsByMode: {
        low: Number(maxIterationsLow) || 25,
        medium: Number(maxIterationsMedium) || 40,
        high: Number(maxIterationsHigh) || 60,
      },
      turnBudget: {
        mode: turnBudgetMode,
        tokensByMode: {
          low: Number(turnBudgetLow) || 300000,
          medium: Number(turnBudgetMedium) || 800000,
          high: Number(turnBudgetHigh) || 2000000,
        },
        warningPercent: Number(turnBudgetWarningPercent) || 80,
      },
      promptBudget,
      webTools,
      browserTools,
      remote,
    });
  };

  const toggleRiskLevel = (riskLevel: CodeAgentRiskLevel) => {
    setBlockedRiskLevels((current) =>
      current.includes(riskLevel)
        ? current.filter((item) => item !== riskLevel)
        : [...current, riskLevel],
    );
  };

  const toggleSkillRiskLevel = (riskLevel: CodeAgentRiskLevel) => {
    setBlockedSkillRiskLevels((current) =>
      current.includes(riskLevel)
        ? current.filter((item) => item !== riskLevel)
        : [...current, riskLevel],
    );
  };

  const toggleSkillSource = (source: CodeAgentSkillSource) => {
    setBlockedSkillSources((current) =>
      current.includes(source) ? current.filter((item) => item !== source) : [...current, source],
    );
  };

  const handleRevokeAll = async () => {
    await revokeAllMutation.mutateAsync();
    setRevokeDialogOpen(false);
  };

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      {/* Header */}
      <Box className="mb-6">
        <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
          <Power className="text-red-500" /> Code Agent Kill Switches
        </Typography>
        <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
          Emergency controls for the Code Agent system. Use with extreme caution.
        </Typography>
      </Box>

      {isLoading ? (
        <Box className="flex items-center justify-center p-12">
          <CircularProgress />
        </Box>
      ) : (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Global Controls */}
        <Card className="dark:bg-slate-800 shadow-sm border border-gray-200 dark:border-slate-700">
          <CardContent className="p-6">
            <Typography variant="h6" className="font-bold flex items-center gap-2 mb-4 text-gray-900 dark:text-slate-100">
              <ShieldAlert size={20} className="text-amber-500" />
              Global Controls
            </Typography>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Typography className="font-semibold text-gray-900 dark:text-slate-100">Global Code Agent Status</Typography>
                  <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                    If disabled, all new requests will be rejected and active agents will pause.
                  </Typography>
                </div>
                <Switch 
                  checked={globalEnabled} 
                  onChange={(e) => setGlobalEnabled(e.target.checked)} 
                  color="primary"
                />
              </div>
              
              <Divider className="dark:border-slate-700" />

              <div className="flex items-center justify-between">
                <div>
                  <Typography className="font-semibold text-gray-900 dark:text-slate-100">Force Safe Mode</Typography>
                  <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                    Require explicit user approval for ALL tool calls across all workspaces, overriding local settings.
                  </Typography>
                </div>
                <Switch 
                  checked={forceSafeMode} 
                  onChange={(e) => setForceSafeMode(e.target.checked)} 
                  color="warning"
                />
              </div>

              <Divider className="dark:border-slate-700" />

              <div>
                <Typography className="font-semibold mb-2 text-gray-900 dark:text-slate-100">Blocked Tool Risk Levels</Typography>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {RISK_LEVELS.map((riskLevel) => (
                    <FormControlLabel
                      key={riskLevel}
                      control={
                        <Checkbox
                          checked={blockedRiskLevels.includes(riskLevel)}
                          onChange={() => toggleRiskLevel(riskLevel)}
                        />
                      }
                      label={riskLevel}
                      className="text-gray-700 dark:text-slate-300"
                    />
                  ))}
                </div>
                <Typography variant="body2" className="text-gray-500 dark:text-slate-400 mt-2">
                  Matching tool calls are denied before approval or dispatch.
                </Typography>
              </div>

              <Divider className="dark:border-slate-700" />

              <div>
                <Typography className="font-semibold mb-2 text-gray-900 dark:text-slate-100">CLI Version Policy</Typography>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <TextField 
                    size="small" 
                    label="Min CLI version"
                    value={minCliVersion}
                    onChange={(e) => setMinCliVersion(e.target.value)}
                    sx={codeAgentFieldSx}
                    placeholder="e.g. 1.2.0"
                  />
                  <TextField
                    size="small"
                    label="Recommended CLI version (warn only)"
                    value={recommendedCliVersion}
                    onChange={(e) => setRecommendedCliVersion(e.target.value)}
                    sx={codeAgentFieldSx}
                    placeholder="e.g. 1.3.0"
                  />
                </div>
                <Typography variant="body2" className="text-gray-500 dark:text-slate-400 mt-2">
                  Below Min = blocked; below Recommended = warning + auto-update.
                </Typography>
              </div>

              <Divider className="dark:border-slate-700" />

              <div>
                <Typography className="font-semibold mb-1 text-gray-900 dark:text-slate-100">
                  Max Iterations by Execution Mode
                </Typography>
                <Typography variant="body2" className="text-gray-500 dark:text-slate-400 mb-3">
                  Limit continuous tool execution loops per turn before saving a checkpoint and gracefully pausing for /continue confirmation.
                </Typography>
                <div className="grid grid-cols-3 gap-3">
                  <FormattedNumberTextField
                    size="small"
                    label="Low (Quick)"
                    value={maxIterationsLow}
                    onValueChange={(value) => setMaxIterationsLow(value ?? 0)}
                    sx={codeAgentFieldSx}
                  />
                  <FormattedNumberTextField
                    size="small"
                    label="Medium (Standard)"
                    value={maxIterationsMedium}
                    onValueChange={(value) => setMaxIterationsMedium(value ?? 0)}
                    sx={codeAgentFieldSx}
                  />
                  <FormattedNumberTextField
                    size="small"
                    label="High (Complex)"
                    value={maxIterationsHigh}
                    onValueChange={(value) => setMaxIterationsHigh(value ?? 0)}
                    sx={codeAgentFieldSx}
                  />
                </div>
              </div>

              <Divider className="dark:border-slate-700" />

              <div>
                <Typography className="font-semibold mb-1 text-gray-900 dark:text-slate-100">
                  Turn Budget (K2)
                </Typography>
                <Typography variant="body2" className="text-gray-500 dark:text-slate-400 mb-3">
                  Tokens mode caps each turn by advance tokens (quota units the gateway settles), sends one warning at the threshold and asks the user only when the budget is exceeded. Iterations mode keeps the legacy step cap above.
                </Typography>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <TextField
                    select
                    size="small"
                    label="Budget basis"
                    value={turnBudgetMode}
                    onChange={(e) => setTurnBudgetMode(e.target.value as 'tokens' | 'iterations')}
                    sx={codeAgentFieldSx}
                    SelectProps={{ native: true }}
                  >
                    <option value="tokens">Advance credits per turn</option>
                    <option value="iterations">Iterations (legacy)</option>
                  </TextField>
                  <FormattedNumberTextField
                    size="small"
                    label="Warning at (%)"
                    value={turnBudgetWarningPercent}
                    onValueChange={(value) => setTurnBudgetWarningPercent(value ?? 0)}
                    sx={codeAgentFieldSx}
                    inputProps={{ min: 1, max: 99 }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <FormattedNumberTextField
                    size="small"
                    label="Low (credits)"
                    value={turnBudgetLow}
                    onValueChange={(value) => setTurnBudgetLow(value ?? 0)}
                    sx={codeAgentFieldSx}
                  />
                  <FormattedNumberTextField
                    size="small"
                    label="Medium (credits)"
                    value={turnBudgetMedium}
                    onValueChange={(value) => setTurnBudgetMedium(value ?? 0)}
                    sx={codeAgentFieldSx}
                  />
                  <FormattedNumberTextField
                    size="small"
                    label="High (credits)"
                    value={turnBudgetHigh}
                    onValueChange={(value) => setTurnBudgetHigh(value ?? 0)}
                    sx={codeAgentFieldSx}
                  />
                </div>
              </div>

              <CodeAgentPromptBudgetSection
                promptBudget={promptBudget}
                webTools={webTools}
                onPromptBudgetChange={setPromptBudget}
                onWebToolsChange={setWebTools}
              />

              <CodeAgentBrowserToolsSection browserTools={browserTools} onChange={setBrowserTools} />

              <CodeAgentRemoteSection remote={remote} onChange={setRemote} />

              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSaveSettings}
                disabled={updateMutation.isPending}
                className="mt-4"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="dark:bg-slate-800 shadow-sm border border-gray-200 dark:border-slate-700">
          <CardContent className="p-6">
            <Typography variant="h6" className="font-bold flex items-center gap-2 mb-4 text-gray-900 dark:text-slate-100">
              <ShieldAlert size={20} className="text-blue-500" />
              Skill Controls
            </Typography>

            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Typography className="font-semibold text-gray-900 dark:text-slate-100">Local Skills</Typography>
                    <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                      Allow CLI-supplied local skill inventory.
                    </Typography>
                  </div>
                  <Switch
                    checked={localSkillsEnabled}
                    onChange={(e) => setLocalSkillsEnabled(e.target.checked)}
                    color="primary"
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Typography className="font-semibold text-gray-900 dark:text-slate-100">User Skills</Typography>
                    <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                      Allow user-scope skills after path redaction.
                    </Typography>
                  </div>
                  <Switch
                    checked={userSkillsEnabled}
                    onChange={(e) => setUserSkillsEnabled(e.target.checked)}
                    color="primary"
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Typography className="font-semibold text-gray-900 dark:text-slate-100">Implicit Activation</Typography>
                    <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                      Allow auto skills to be selected by the model.
                    </Typography>
                  </div>
                  <Switch
                    checked={implicitSkillActivationEnabled}
                    onChange={(e) => setImplicitSkillActivationEnabled(e.target.checked)}
                    color="primary"
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Typography className="font-semibold text-gray-900 dark:text-slate-100">Reference Loading</Typography>
                    <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                      Allow selected references and assets to load through load_skill.
                    </Typography>
                  </div>
                  <Switch
                    checked={skillReferenceLoadingEnabled}
                    onChange={(e) => setSkillReferenceLoadingEnabled(e.target.checked)}
                    color="primary"
                  />
                </div>
              </div>

              <Divider className="dark:border-slate-700" />

              <div>
                <Typography className="font-semibold mb-2 text-gray-900 dark:text-slate-100">Blocked Skill Sources</Typography>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SKILL_SOURCES.map((source) => (
                    <FormControlLabel
                      key={source}
                      control={
                        <Checkbox
                          checked={blockedSkillSources.includes(source)}
                          onChange={() => toggleSkillSource(source)}
                        />
                      }
                      label={source}
                      className="text-gray-700 dark:text-slate-300"
                    />
                  ))}
                </div>
              </div>

              <Divider className="dark:border-slate-700" />

              <div>
                <Typography className="font-semibold mb-2 text-gray-900 dark:text-slate-100">Blocked Skill Risk Levels</Typography>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {RISK_LEVELS.map((riskLevel) => (
                    <FormControlLabel
                      key={riskLevel}
                      control={
                        <Checkbox
                          checked={blockedSkillRiskLevels.includes(riskLevel)}
                          onChange={() => toggleSkillRiskLevel(riskLevel)}
                        />
                      }
                      label={riskLevel}
                      className="text-gray-700 dark:text-slate-300"
                    />
                  ))}
                </div>
              </div>

              <Divider className="dark:border-slate-700" />

              <div className="flex items-center justify-between gap-4">
                <div>
                  <Typography className="font-semibold text-gray-900 dark:text-slate-100">Block Skills With Scripts</Typography>
                  <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                    Hide and reject skills that include a scripts directory.
                  </Typography>
                </div>
                <Switch
                  checked={blockedSkillsWithScripts}
                  onChange={(e) => setBlockedSkillsWithScripts(e.target.checked)}
                  color="warning"
                />
              </div>

              <Divider className="dark:border-slate-700" />

              <div className="grid grid-cols-1 gap-4">
                <TextField
                  label="Blocked skill names"
                  value={blockedSkillNames}
                  onChange={(e) => setBlockedSkillNames(e.target.value)}
                  multiline
                  minRows={3}
                  size="small"
                  sx={codeAgentFieldSx}
                  helperText="One name or qualified name per line."
                />
                <TextField
                  label="Blocked skill ids"
                  value={blockedSkillIds}
                  onChange={(e) => setBlockedSkillIds(e.target.value)}
                  multiline
                  minRows={3}
                  size="small"
                  sx={codeAgentFieldSx}
                  helperText="One exact skill id per line."
                />
                <TextField
                  label="Blocked project skill paths"
                  value={blockedProjectSkillPaths}
                  onChange={(e) => setBlockedProjectSkillPaths(e.target.value)}
                  multiline
                  minRows={3}
                  size="small"
                  sx={codeAgentFieldSx}
                  helperText="Project-relative SKILL.md paths only."
                />
                <TextField
                  label="Allowed plugin names"
                  value={allowedPluginNames}
                  onChange={(e) => setAllowedPluginNames(e.target.value)}
                  multiline
                  minRows={3}
                  size="small"
                  sx={codeAgentFieldSx}
                  helperText="Optional allow-list. Leave empty to allow any plugin not explicitly blocked."
                />
                <TextField
                  label="Blocked plugin names"
                  value={blockedPluginNames}
                  onChange={(e) => setBlockedPluginNames(e.target.value)}
                  multiline
                  minRows={3}
                  size="small"
                  sx={codeAgentFieldSx}
                  helperText="One plugin name per line. Blocked plugins always win over the allow-list."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Emergency Actions */}
        <Card className="dark:bg-slate-800 shadow-sm border border-red-200 dark:border-red-900/30">
          <CardContent className="p-6">
            <Typography variant="h6" className="font-bold flex items-center gap-2 mb-4 text-red-600 dark:text-red-400">
              <AlertTriangle size={20} />
              Emergency Actions
            </Typography>
            
            <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-lg border border-red-100 dark:border-red-900/20 mb-6">
              <Typography className="font-semibold text-red-800 dark:text-red-300 mb-1">
                Revoke All Active Sessions
              </Typography>
              <Typography variant="body2" className="text-red-600 dark:text-red-400 mb-4">
                This will immediately terminate all active Code Agent tasks across all workspaces globally. 
                Users will need to restart their commands. Data loss may occur.
              </Typography>
              <Button 
                variant="contained" 
                color="error" 
                onClick={() => setRevokeDialogOpen(true)}
                disabled={revokeAllMutation.isPending}
              >
                Revoke All Sessions
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={() => setRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="bg-red-600 text-white">
          <Typography variant="h6" className="font-bold flex items-center gap-2">
            <AlertTriangle size={20} /> Extreme Danger
          </Typography>
        </DialogTitle>
        <DialogContent className="mt-4">
          <Typography className="font-bold text-gray-900 dark:text-slate-100 mb-2">
            You are about to revoke ALL active Code Agent sessions globally.
          </Typography>
          <Typography className="text-gray-700 dark:text-slate-300">
            This action cannot be undone. All active LLM generation loops will be interrupted, and all active terminal connections will be closed.
          </Typography>
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button
            onClick={() => setRevokeDialogOpen(false)}
            className="text-gray-600 dark:text-slate-400"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRevokeAll}
            variant="contained"
            color="error"
            disabled={revokeAllMutation.isPending}
          >
            {revokeAllMutation.isPending ? 'Revoking...' : 'I understand, Revoke All'}
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </Box>
  );
};
